import { Button, Group, Modal, Stack, Switch, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useCallback } from 'react';
import { UsStateSelect } from '@/components/UsStateSelect';
import {
  defaultPayerFormValues,
  payerSchema,
  type PayerFormInput,
} from '@/schemas/payer.schema';

export interface AddPayerModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: PayerFormInput) => void | Promise<void>;
  initialValues?: PayerFormInput;
  editing?: boolean;
}

export function AddPayerModal({
  opened,
  onClose,
  onSubmit,
  initialValues = defaultPayerFormValues,
  editing = false,
}: AddPayerModalProps): JSX.Element {
  const form = useForm<PayerFormInput>({
    initialValues,
    validate: zod4Resolver(payerSchema),
  });

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleSubmit = useCallback(
    async (values: PayerFormInput) => {
      await onSubmit(values);
      form.reset();
      onClose();
    },
    [form, onClose, onSubmit],
  );

  return (
    <Modal opened={opened} onClose={handleClose} title={editing ? 'Edit Payer' : 'Add Payer'} centered>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="Payer name"
            withAsterisk
            {...form.getInputProps('name')}
          />
          <Switch
            label="Active"
            color="gold"
            {...form.getInputProps('active', { type: 'checkbox' })}
          />
          <UsStateSelect
            label="State"
            placeholder="Optional"
            clearable
            value={form.values.state ?? ''}
            onChange={(value) => form.setFieldValue('state', value)}
            error={form.errors.state}
          />
          <TextInput
            label="Sohar ID"
            placeholder="Optional"
            {...form.getInputProps('soharId')}
          />
          <TextInput
            label="Stedi ID"
            placeholder="Optional"
            {...form.getInputProps('stediNetworkId')}
          />
          <TextInput
            label="Verifiable Medicaid ID"
            placeholder="Optional"
            {...form.getInputProps('verifiableMedicaidId')}
          />
          <TextInput
            label="Verifiable Medicare ID"
            placeholder="Optional"
            {...form.getInputProps('verifiableMedicareId')}
          />
          <TextInput
            label="Verifiable Commercial ID"
            placeholder="Optional"
            {...form.getInputProps('verifiableCommercialId')}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit">{editing ? 'Save Changes' : 'Add Payer'}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
